import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/data/content";
import { submitLead, track } from "@/lib/api";

const PROJECT_TYPES = ["New Construction", "Retrofit", "Commercial", "Industrial", "Office", "High-Bay", "Outdoor", "Other"];

const Field = ({ label, name, type = "text", required = false, value, onChange, testId }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5 block">
      {label} {required && <span className="text-signal">*</span>}
    </span>
    <input
      type={type}
      name={name}
      required={required}
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="w-full border border-zinc-200 bg-bone px-4 py-3 text-sm text-ink outline-none focus:border-signal transition-colors"
    />
  </label>
);

export default function Contact() {
  const [params] = useSearchParams();
  const initType = params.get("type") === "quote" ? "quote" : "demo";
  const [type, setType] = useState(initType);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    firstName: "", lastName: "", company: "", email: "", phone: "",
    projectType: "", projectSize: "", location: "", message: "",
  });

  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitLead({ ...form, type });
      track("lead_submit", { type });
      setSent(true);
      toast.success("Request received — our team will reach out shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-bone min-h-screen" data-testid="contact-page">
      <SEO
        title="Contact — Request a Demo or Quote"
        description="Request an Amaya Pro demo or quote. Tell us about your project and our team will design the right Network Lighting Control system."
        path="/contact"
      />
      <PageHero
        overline="Contact"
        title="Let's build a smarter lighting system."
        sub="Tell us about your project — we'll respond with the right products, a demo or a quote."
      />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <Reveal className="lg:col-span-2">
          <div className="bg-white border border-zinc-200 p-6 md:p-10">
            {sent ? (
              <div className="text-center py-16" data-testid="form-success">
                <CheckCircle2 size={44} className="text-signal mx-auto" />
                <h2 className="font-display text-2xl font-bold text-ink mt-5">Request received.</h2>
                <p className="text-zinc-500 mt-2 text-sm">Our team will reach out shortly. For anything urgent, call {SITE.phone}.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form">
                <div className="flex gap-3 mb-8" role="group" aria-label="Request type">
                  {[["demo", "Request a Demo"], ["quote", "Get a Quote"]].map(([v, label]) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setType(v)}
                      aria-pressed={type === v}
                      data-testid={`type-${v}-btn`}
                      className={`flex-1 px-4 py-3 text-sm font-medium border transition-colors ${
                        type === v ? "bg-forest text-white border-forest" : "bg-bone text-zinc-600 border-zinc-200 hover:border-zinc-400"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="First Name" name="firstName" required value={form.firstName} onChange={set} testId="field-first-name" />
                  <Field label="Last Name" name="lastName" required value={form.lastName} onChange={set} testId="field-last-name" />
                  <Field label="Company" name="company" value={form.company} onChange={set} testId="field-company" />
                  <Field label="Work Email" name="email" type="email" required value={form.email} onChange={set} testId="field-email" />
                  <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={set} testId="field-phone" />
                  <label className="block">
                    <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Project Type</span>
                    <select name="projectType" value={form.projectType} onChange={set} data-testid="field-project-type" className="w-full border border-zinc-200 bg-bone px-4 py-3 text-sm text-ink outline-none focus:border-signal">
                      <option value="">Select…</option>
                      {PROJECT_TYPES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </label>
                  <Field label="Project Size" name="projectSize" value={form.projectSize} onChange={set} testId="field-project-size" />
                  <Field label="Location" name="location" value={form.location} onChange={set} testId="field-location" />
                </div>
                <label className="block mt-5">
                  <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Message</span>
                  <textarea
                    name="message"
                    rows={4}
                    value={form.message}
                    onChange={set}
                    data-testid="field-message"
                    className="w-full border border-zinc-200 bg-bone px-4 py-3 text-sm text-ink outline-none focus:border-signal transition-colors resize-y"
                  />
                </label>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-btn"
                  className="mt-8 px-8 py-3.5 bg-signal text-white font-medium hover:bg-[#5c8f23] transition-colors flex items-center gap-2 disabled:opacity-60"
                >
                  {sending ? "Sending…" : type === "demo" ? "Request a Demo" : "Get a Quote"} <ArrowRight size={16} />
                </button>
              </form>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <aside className="space-y-6">
            <div className="bg-forest text-white p-8" data-testid="contact-info-card">
              <h2 className="font-display text-xl font-bold">Talk to sales</h2>
              <ul className="mt-6 space-y-4 text-sm">
                <li className="flex items-center gap-3"><Phone size={15} className="text-signal" /> <a href={`tel:${SITE.phone}`} className="hover:text-signal transition-colors">{SITE.phone}</a></li>
                <li className="flex items-center gap-3"><Mail size={15} className="text-signal" /> <a href={`mailto:${SITE.email}`} className="hover:text-signal transition-colors break-all">{SITE.email}</a></li>
                <li className="flex items-start gap-3"><MapPin size={15} className="text-signal mt-0.5" /> <span className="text-white/70">{SITE.address}</span></li>
              </ul>
              <p className="text-xs text-white/50 mt-6 pt-5 border-t border-white/15">Technical support: {SITE.supportPhone}</p>
            </div>
            <div className="bg-white border border-zinc-200 p-8">
              <h2 className="font-display text-xl font-bold text-ink">What happens next</h2>
              <ol className="mt-5 space-y-4 text-sm text-zinc-600">
                {["We review your project details", "A specialist reaches out within one business day", "We schedule your demo or prepare your quote"].map((s, i) => (
                  <li key={s} className="flex gap-3">
                    <span className="font-mono text-xs text-signal pt-0.5">{String(i + 1).padStart(2, "0")}</span> {s}
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </Reveal>
      </section>
    </div>
  );
}
